import React, { useRef, useState } from 'react';
import { Carousel, Button, Space } from 'antd';
import { AppleOutlined, AndroidOutlined } from '@ant-design/icons';
import { GOOGLE_PLAY_URL, APP_STORE_URL } from '../../common/constants';
import './Banner.css';

// Composant pour l'encadré promotionnel
const PromoBox = ({ banner }) => {
    if (!banner.showPromoBox || !banner.promoMessage) return null;

    const bgColor = banner.promoBackgroundColor || '#D4A853';
    const textColor = banner.promoTextColor || '#ffffff';

    return (
        <div
            className="promo-box"
            style={{
                backgroundColor: bgColor,
                color: textColor,
            }}
        >
            <div className="promo-box-content">
                <p className="promo-message" style={{ color: textColor }}>
                    {banner.promoMessage}
                </p>
                {banner.promoSubtext && (
                    <p className="promo-subtext" style={{ color: textColor, opacity: 0.9 }}>
                        {banner.promoSubtext}
                    </p>
                )}
            </div>
        </div>
    );
};

const Banner = ({ banners = [], type = 'hero' }) => {
    const carouselRef = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    if (!banners || banners.length === 0) {
        // Default banner if no dynamic banners
        if (type === 'hero') {
            return (
                <div className="banner banner-hero banner-default">
                    <div className="banner-content">
                        <h1>Téléchargez TIGOON Connect</h1>
                        <p>Tous vos services en une seule application : courses, livraisons, boutique</p>
                        <Space size={16} className="banner-buttons">
                            <Button
                                type="primary"
                                size="large"
                                icon={<AndroidOutlined />}
                                href={GOOGLE_PLAY_URL}
                                target="_blank"
                            >
                                Google Play
                            </Button>
                            <Button
                                size="large"
                                icon={<AppleOutlined />}
                                href={APP_STORE_URL}
                                target="_blank"
                                className="btn-appstore"
                            >
                                App Store
                            </Button>
                        </Space>
                    </div>
                </div>
            );
        }
        return null;
    }

    if (banners.length === 1) {
        const banner = banners[0];
        return (
            <div className={`banner banner-${type}`}>
                <a
                    href={banner.linkUrl || '#'}
                    target={banner.linkUrl ? '_blank' : '_self'}
                    rel="noopener noreferrer"
                    className="banner-link"
                >
                    <img src={banner.imageUrl} alt={banner.title} className="banner-image" />
                    {/* Overlay titre/description - masqué pour les hero banners */}
                    {type !== 'hero' && banner.title && (
                        <div className="banner-overlay">
                            <h2>{banner.title}</h2>
                            {banner.subtitle && <p>{banner.subtitle}</p>}
                        </div>
                    )}
                </a>
                {/* Encadré promotionnel pour hero banners */}
                {type === 'hero' && <PromoBox banner={banner} />}
            </div>
        );
    }

    const handleSlideChange = (current) => {
        setCurrentSlide(current);
    };

    const goToSlide = (index) => {
        carouselRef.current?.goTo(index);
    };

    return (
        <div className={`banner banner-${type} banner-carousel`}>
            <Carousel
                ref={carouselRef}
                autoplay
                autoplaySpeed={5000}
                beforeChange={(_, next) => handleSlideChange(next)}
                dots={false}
                effect="fade"
            >
                {banners.map((banner) => (
                    <div key={banner._id || banner.id}>
                        <a
                            href={banner.linkUrl || '#'}
                            target={banner.linkUrl ? '_blank' : '_self'}
                            rel="noopener noreferrer"
                            className="banner-link"
                        >
                            <img src={banner.imageUrl} alt={banner.title} className="banner-image" />
                            {/* Overlay titre/description - masqué pour les hero banners */}
                            {type !== 'hero' && banner.title && (
                                <div className="banner-overlay">
                                    <h2>{banner.title}</h2>
                                    {banner.subtitle && <p>{banner.subtitle}</p>}
                                </div>
                            )}
                        </a>
                    </div>
                ))}
            </Carousel>

            {/* Indicateurs de pagination personnalisés */}
            <div className="carousel-indicators">
                <div className="carousel-dots">
                    {banners.map((_, index) => (
                        <button
                            key={index}
                            className={`carousel-dot ${currentSlide === index ? 'active' : ''}`}
                            onClick={() => goToSlide(index)}
                            aria-label={`Slide ${index + 1}`}
                        />
                    ))}
                </div>
            </div>

            {/* Encadré promotionnel pour hero banners */}
            {type === 'hero' && banners[currentSlide] && (
                <PromoBox banner={banners[currentSlide]} />
            )}
        </div>
    );
};

export default Banner;
